// The video seek bar (shared by the inline player and the phone viewer).
//
// Not an `<input type="range">`: the buffered range is shown on the same track.
// While pressed and dragged only the look moves; `onSeek` runs once, on
// release (writing currentTime on every move outruns loading and stalls).
//
// Presses are **not passed to the parent** (`stopPropagation`): below it wait
// the gesture layer (viewer) or the editor (inline player).
import { m } from "@trameria/i18n";
import { useRef, useState } from "react";
import { formatVideoTime, seekRatio } from "./video-controls";

export function SeekBar({
  time,
  duration,
  buffered,
  onSeek,
  rotated = false,
}: {
  time: number;
  duration: number;
  buffered: number;
  onSeek: (t: number) => void;
  /** While the parent is turned 90° clockwise, the visual left is the device's top (measure vertically). */
  rotated?: boolean;
}) {
  const bar = useRef<HTMLDivElement>(null);
  const pointer = useRef<number | null>(null);
  const [preview, setPreview] = useState<number | null>(null);
  const known = Number.isFinite(duration) && duration > 0;
  const shown = preview ?? time;
  const pct = (v: number) =>
    known ? `${(Math.min(Math.max(v, 0), duration) / duration) * 100}%` : "0%";
  const at = (clientX: number, clientY: number) => {
    const r = bar.current?.getBoundingClientRect();
    if (!r) return 0;
    const ratio = rotated
      ? seekRatio(clientY, r.top, r.height)
      : seekRatio(clientX, r.left, r.width);
    return ratio * duration;
  };
  return (
    <div
      ref={bar}
      role="slider"
      tabIndex={-1}
      aria-label={m.media_video_seek()}
      aria-valuemin={0}
      aria-valuemax={known ? Math.floor(duration) : 0}
      aria-valuenow={Math.floor(shown)}
      aria-valuetext={`${formatVideoTime(shown)} / ${formatVideoTime(duration)}`}
      data-seekbar=""
      className="relative h-5 w-full cursor-pointer touch-none"
      // Pressing does not take the focus (keys go to the player's root).
      onMouseDown={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (!known) return;
        pointer.current = e.pointerId;
        // Guarded: happy-dom has no pointer capture.
        if (typeof e.currentTarget.setPointerCapture === "function")
          e.currentTarget.setPointerCapture(e.pointerId);
        setPreview(at(e.clientX, e.clientY));
      }}
      onPointerMove={(e) => {
        if (pointer.current !== e.pointerId) return;
        setPreview(at(e.clientX, e.clientY));
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        if (pointer.current !== e.pointerId) return;
        pointer.current = null;
        onSeek(at(e.clientX, e.clientY));
        setPreview(null);
      }}
      onPointerCancel={() => {
        pointer.current = null;
        setPreview(null);
      }}
    >
      <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-white/30">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-white/45"
          style={{ width: pct(buffered) }}
        />
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-accent"
          style={{ width: pct(shown) }}
        />
      </div>
      <div
        className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow"
        style={{ left: pct(shown) }}
      />
    </div>
  );
}
